import React, { useState, useEffect, useRef } from 'react';
import ClariMeLogo, { EmbraceMarkSmall } from './ClariMeLogo';
import AppStoreBadge, { APP_STORE_URL } from './AppStoreBadge';

// ── Scroll reveal hook ──────────────────────────────────────────────
function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px', ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}

// ── Reveal wrapper ──────────────────────────────────────────────────
function Reveal({ children, className = '', delay = 0 }) {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 0.7s cubic-bezier(.16,1,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ── Inline icons (landing page only) ───────────────────────────────

const MicLargeIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

const SparkleIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const ShieldIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const HeartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

// ── Phone Mockup Component ──────────────────────────────────────────
function PhoneMockup() {
  const [seconds, setSeconds] = useState(0);
  const [recording, setRecording] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds(s => (s + 1) % 600);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');

  // Animated waveform bars
  const bars = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="relative" style={{ perspective: '1200px' }}>
      {/* Glow behind phone */}
      <div className="absolute inset-0 blur-3xl opacity-40 bg-gradient-to-br from-teal-400 via-cyan-300 to-teal-500 rounded-full scale-75 -z-10" />

      {/* Phone frame */}
      <div
        className="relative w-[260px] sm:w-[280px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/40"
        style={{ transform: 'rotateY(-4deg) rotateX(2deg)' }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-slate-900 rounded-b-2xl z-20" />

        {/* Screen */}
        <div className="bg-white rounded-[2.3rem] overflow-hidden">
          {/* Status bar */}
          <div className="bg-white pt-8 pb-2 px-6 flex items-center justify-between">
            <span className="text-[10px] text-slate-400 font-medium">9:41 AM</span>
            <div className="flex gap-1">
              <div className="w-3 h-2 rounded-sm bg-slate-300" />
              <div className="w-3 h-2 rounded-sm bg-slate-300" />
              <div className="w-4 h-2 rounded-sm bg-slate-400" />
            </div>
          </div>

          {/* App header */}
          <div className="px-5 pt-2 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <EmbraceMarkSmall className="w-5 h-5" id="phone-logo" />
                <span className="text-xs tracking-[-0.02em]" style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}>
                  <span className="font-normal text-teal-600">clari</span>
                  <span className="font-semibold text-slate-800">me</span>
                </span>
              </div>
              <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center">
                <ShieldIcon className="w-3.5 h-3.5 text-teal-500" />
              </div>
            </div>
          </div>

          {/* Recording card */}
          <div className="px-4 pb-3">
            <div className="bg-gradient-to-br from-slate-50 to-teal-50/50 rounded-2xl p-4 border border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs font-semibold text-slate-700">Recording Appointment</span>
              </div>

              {/* Timer */}
              <div className="text-center mb-4">
                <span className="text-3xl font-mono font-bold text-slate-900 tracking-wider">{mins}:{secs}</span>
              </div>

              {/* Waveform visualization */}
              <div className="flex items-center justify-center gap-[3px] h-10 mb-4">
                {bars.map(i => (
                  <div
                    key={i}
                    className="w-[3px] rounded-full bg-gradient-to-t from-teal-500 to-cyan-400"
                    style={{
                      height: `${12 + Math.sin((seconds * 3 + i * 0.8)) * 8 + Math.cos((seconds * 2 + i * 1.2)) * 6}px`,
                      opacity: 0.5 + Math.sin((seconds * 2 + i) * 0.5) * 0.3,
                      transition: 'height 0.3s ease',
                    }}
                  />
                ))}
              </div>

              {/* Stop button */}
              <div className="flex justify-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/25 ring-4 ring-red-100">
                  <div className="w-5 h-5 rounded-sm bg-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Doctor info pill */}
          <div className="px-4 pb-3">
            <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-sm">🩺</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">Dr. Sarah Chen</p>
                <p className="text-[10px] text-slate-400">Oncology · Memorial Hospital</p>
              </div>
            </div>
          </div>

          {/* Bottom nav hint */}
          <div className="px-6 pt-2 pb-6 flex justify-center gap-8">
            <div className="flex flex-col items-center gap-1">
              <div className="w-5 h-5 rounded-md bg-teal-500 flex items-center justify-center">
                <MicLargeIcon className="w-3 h-3 text-white" />
              </div>
              <span className="text-[8px] text-teal-600 font-medium">Record</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center">
                <SparkleIcon className="w-3 h-3 text-slate-400" />
              </div>
              <span className="text-[8px] text-slate-400 font-medium">Visits</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center">
                <UsersIcon className="w-3 h-3 text-slate-400" />
              </div>
              <span className="text-[8px] text-slate-400 font-medium">Care Group</span>
            </div>
          </div>
        </div>

        {/* Home indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-slate-600 rounded-full" />
      </div>
    </div>
  );
}


// ── Landing Page ─────────────────────────────────────────────────────

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* ── App Store announcement bar ───────────────────────── */}
      <a
        href={APP_STORE_URL}
        className="block bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500 text-white text-center text-sm sm:text-[15px] font-semibold py-3 px-4 hover:brightness-105 transition-all"
      >
        <span className="inline-flex items-center gap-2">
          <span aria-hidden="true">🎉</span>
          ClariMe is now on the Apple App Store
          <span className="hidden sm:inline text-white/85 font-medium">· Download it free</span>
          <span aria-hidden="true" className="font-bold">&rarr;</span>
        </span>
      </a>

      {/* ── Nav ──────────────────────────────────────────────── */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-sm shadow-slate-200/60 border-b border-slate-100'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <ClariMeLogo size="default" variant="light" />
          <div className="flex items-center gap-6">
            <a href="#how-it-works" className="hidden md:inline text-sm text-slate-500 hover:text-slate-800 transition-colors">
              How It Works
            </a>
            <a href="#privacy" className="hidden md:inline text-sm text-slate-500 hover:text-slate-800 transition-colors">
              Privacy
            </a>
            <a href="#/providers" className="hidden md:inline text-sm text-slate-500 hover:text-slate-800 transition-colors">
              For Providers
            </a>
            <a
              href="#/join"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-xl shadow-md shadow-teal-500/20 hover:shadow-lg hover:shadow-teal-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-teal-50 via-cyan-50/80 to-white rounded-full opacity-70 -translate-y-1/2" />
          <div className="absolute top-40 -right-20 w-72 h-72 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-full opacity-40 blur-2xl" />
          <div className="absolute bottom-0 -left-20 w-60 h-60 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-full opacity-50 blur-2xl" />
        </div>

        <div className="max-w-6xl mx-auto px-6 pt-12 pb-16 sm:pt-20 sm:pb-24">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left: Copy */}
            <div className="flex-1 text-center lg:text-left max-w-xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-100 rounded-full text-teal-700 text-sm font-medium mb-8 animate-fade-in">
                <HeartIcon className="w-4 h-4" />
                Built for patients and the people who care for them
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-slate-900 leading-[1.1] mb-6 tracking-tight">
                Your doctor visit,{' '}
                <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-400 bg-clip-text text-transparent">clarified</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 leading-relaxed mb-10 max-w-lg mx-auto lg:mx-0">
                Record your appointments, get a clear summary of what was said, and share it with the family members who help you stay healthy.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <AppStoreBadge className="w-full sm:w-auto justify-center" />
                <a
                  href="#how-it-works"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-600 text-lg font-medium rounded-2xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
                >
                  See How It Works
                </a>
              </div>
              <p className="text-sm text-slate-400 mt-5">
                On Android or a computer?{' '}
                <a href="#/join" className="text-teal-600 font-semibold hover:text-teal-700 underline decoration-teal-200 underline-offset-2">
                  Use ClariMe in your browser
                </a>
              </p>
            </div>

            {/* Right: Phone Mockup */}
            <div className="flex-shrink-0 relative">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── The Problem ──────────────────────────────────────── */}
      <section className="relative py-20 px-6 bg-slate-50/80 border-t border-slate-100">
        <Reveal>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
              Most patients forget up to{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">80%</span>
                <span className="absolute -bottom-1 left-0 right-0 h-2 bg-gradient-to-r from-amber-200 to-orange-200 rounded-full" />
              </span>
              {' '}of what their doctor says
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              When you're anxious, in pain, or processing a diagnosis, it's hard to take everything in. And when your family asks "what did the doctor say?" — you're left piecing it together from memory.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ── How It Works ─────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 px-6 scroll-mt-20 border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                Three taps. Full clarity.
              </h2>
              <p className="text-lg text-slate-400">Here's how ClariMe works.</p>
            </div>
          </Reveal>

          <div className="relative grid md:grid-cols-3 gap-8 sm:gap-10">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-[4.5rem] left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-teal-200 via-violet-200 to-blue-200 z-0" />

            {/* Step 1 */}
            <Reveal delay={0}>
              <div className="relative text-center group">
                <div className="relative z-10 w-24 h-24 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-[1.75rem] flex items-center justify-center mx-auto mb-6 shadow-sm border border-teal-100/60 group-hover:shadow-md group-hover:shadow-teal-100 group-hover:scale-105 transition-all duration-300">
                  <MicLargeIcon className="w-11 h-11 text-teal-600" />
                </div>
                <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-sm font-bold rounded-full mb-4 shadow-md shadow-teal-500/20">1</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Record your visit</h3>
                <p className="text-slate-500 leading-relaxed">
                  Tap "Record" when your appointment starts. ClariMe listens quietly in the background — no notes required.
                </p>
              </div>
            </Reveal>

            {/* Step 2 */}
            <Reveal delay={120}>
              <div className="relative text-center group">
                <div className="relative z-10 w-24 h-24 bg-gradient-to-br from-violet-50 to-purple-50 rounded-[1.75rem] flex items-center justify-center mx-auto mb-6 shadow-sm border border-violet-100/60 group-hover:shadow-md group-hover:shadow-violet-100 group-hover:scale-105 transition-all duration-300">
                  <SparkleIcon className="w-11 h-11 text-violet-600" />
                </div>
                <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-sm font-bold rounded-full mb-4 shadow-md shadow-teal-500/20">2</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Get your summary</h3>
                <p className="text-slate-500 leading-relaxed">
                  Key points, medications, follow-ups, and questions are extracted automatically — presented in plain, simple language.
                </p>
              </div>
            </Reveal>

            {/* Step 3 */}
            <Reveal delay={240}>
              <div className="relative text-center group">
                <div className="relative z-10 w-24 h-24 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-[1.75rem] flex items-center justify-center mx-auto mb-6 shadow-sm border border-blue-100/60 group-hover:shadow-md group-hover:shadow-blue-100 group-hover:scale-105 transition-all duration-300">
                  <UsersIcon className="w-11 h-11 text-blue-600" />
                </div>
                <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-sm font-bold rounded-full mb-4 shadow-md shadow-teal-500/20">3</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Share with family</h3>
                <p className="text-slate-500 leading-relaxed">
                  Send the summary to your Care Group by email, or share it by text with anyone. Everyone stays informed, even if they couldn't be there.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Who It's For ─────────────────────────────────────── */}
      <section className="py-20 px-6 bg-slate-50/80 border-t border-slate-100">

        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                Built for the moments that matter most
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <Reveal delay={0}>
              <div className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-teal-100 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">👩‍🦳</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">For patients</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  You've got enough on your mind. Let ClariMe remember the details so you can focus on your health.
                </p>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-violet-100 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">👨‍👩‍👧</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">For caregivers & family</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  Can't make it to every appointment? Now you don't have to. Get the full picture, right on your phone.
                </p>
              </div>
            </Reveal>

            <Reveal delay={160}>
              <div className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-rose-100 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">🩺</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">For complex care</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  Cancer treatment, chronic conditions, post-surgical follow-ups — when every detail matters, ClariMe has your back.
                </p>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">📱</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">For anyone with a phone</h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  Family members don't need the app. Summaries arrive as a simple link they can open and read anytime.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Trust & Privacy ──────────────────────────────────── */}
      <section id="privacy" className="py-20 px-6 scroll-mt-20 border-t border-slate-100">
        <Reveal>
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 rounded-[2rem] p-8 sm:p-12 text-white overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-[0.07]">
                <div className="absolute top-0 right-0 w-52 h-52 bg-white rounded-full translate-x-1/3 -translate-y-1/3" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/3 translate-y-1/3" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white rounded-full opacity-30" />
              </div>

              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <ShieldIcon className="w-7 h-7" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold">Your privacy, protected</h2>
                </div>

                <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
                  ClariMe is built with HIPAA-compliant infrastructure from the ground up. Your recordings and summaries are encrypted, access-controlled, and never shared with third parties.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    'HIPAA-compliant infrastructure (AWS)',
                    'Audio encrypted at rest and in transit',
                    'You control who sees your summaries',
                    'Built by a physician who understands',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white/[0.06] rounded-xl px-4 py-3 backdrop-blur-sm">
                      <CheckCircleIcon className="w-5 h-5 text-teal-200 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── Get Started CTA ──────────────────────────────────── */}
      <section id="join" className="py-20 px-6 scroll-mt-20 bg-slate-50/80 border-t border-slate-100">

        <Reveal>
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-100 rounded-full text-teal-700 text-sm font-medium mb-8">
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
              Free to use during our pilot
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Ready to get started?
            </h2>
            <p className="text-lg text-slate-500 leading-relaxed mb-10 max-w-lg mx-auto">
              Set up your account in under a minute. All you need is an email address, and we'll have you ready before your next appointment.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <AppStoreBadge className="w-full sm:w-auto justify-center" />
              <a
                href="#/join"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Create Your Account
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
            <p className="text-sm text-slate-400 mt-5">
              No credit card. iPhone app on the App Store, or use it right in your browser.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="border-t border-slate-100 py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ClariMeLogo size="small" variant="light" />
            <span className="text-slate-300 text-sm">·</span>
            <span className="text-slate-400 text-sm">Your doctor visit, clarified</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <span>&copy; {new Date().getFullYear()} ClariMe Health LLC</span>
            <a href="#/privacy" className="hover:text-slate-600 transition-colors">Privacy</a>
            <a href="#/terms" className="hover:text-slate-600 transition-colors">Terms</a>
            <a href="#/providers" className="hover:text-slate-600 transition-colors">For Providers</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
